import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "modal-wrapper" }
const _hoisted_2 = {
  key: 1,
  class: "columns is-mobile"
}
const _hoisted_3 = { class: "column column__conversation" }
const _hoisted_4 = { class: "column column__cards" }

import BotPreviewMessagingClientContainer from '@/features/bots/components/botTestTool/BotPreviewMessagingClientContainer.vue';
import ContactDetails from '@/features/contacts/components/ContactDetails.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import { BotUserViewModel } from '@/features/bots/models/botUserViewModel';
import FlowSelector from '@/features/bots/components/botTestTool/FlowSelector.vue';
import { FlowListItemViewModel } from '../../models/flowListItemViewModel';
import { LOAD_ANSWERS } from '@/store/answers/actionTypes';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowSelectorItemViewModel } from '../../models/flowSelectorItemViewModel';
import { FlowStatus } from '../../models/flowStatus';
import { BOT_STUDIO_ENABLED } from '@/store/app/featureTypes';
import { AnswerListItemViewModel } from '@/features/bots/models/answers/answerListItemViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import TimeSelector from '@/features/bots/components/botTestTool/TimeSelector.vue';
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';


export default /*@__PURE__*/_defineComponent({
  __name: 'BotPreviewModal',
  props: {
  flowToPreview: {
    type: String,
    default: '',
  },
  answerToPreview: {
    type: String,
    default: '',
  },
},
  emits: ["cancel"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const botUser = ref<BotUserViewModel>({ id: '', data: {} });
const contact = ref<ContactViewModel>({
  id: '',
  type: ContactType.Lead,
  name: '',
  givenName: '',
  surname: '',
  email: '',
  phoneNumber: '',
  avatarUrl: '',
  customAttributes: {},
});
const flows = ref<FlowListItemViewModel[]>([]);
const answers = ref<AnswerListItemViewModel[]>([]);

const flowSelection = ref<FlowSelectorItemViewModel[]>([]);
const answerSelection = ref<FlowSelectorItemViewModel[]>([]);
const currentTime = ref(new Date());
const loaded = ref(false);

const store = useStore();
const isWorkspaceFeatureEnabled = (feature: string, defaultValue = false) =>
  computed(() => store.getters.isWorkspaceFeatureEnabled(feature, defaultValue));

const loadFlows = (doLoad: boolean): Promise<FlowListItemViewModel[]> => store.dispatch(LOAD_FLOWS, doLoad);
const loadAnswers = (doLoad = false): Promise<AnswerListItemViewModel[]> => store.dispatch(LOAD_ANSWERS, doLoad);

const selectedFlows = computed(() => {
  return flows.value
    .filter((flow) => flowSelection.value.find((f) => f.id === flow.id)?.isSelected)
    .map((flow) => ({
      ...flow,
      priority: flowSelection.value.find((f) => f.id === flow.id)?.priority,
    }));
});

const selectedAnswers = computed(() => {
  return answers.value.filter((flow) => answerSelection.value.find((f) => f.id === flow.id)?.isSelected);
});

onMounted(() => getFlows());

async function getFlows() {
  flows.value = await loadFlows(false);
  answers.value = await loadAnswers(false);
  enableCurrentFlowOrAnswer();
  const botStudioEnabled = isWorkspaceFeatureEnabled(BOT_STUDIO_ENABLED, true);

  flowSelection.value = flows.value
    .sort((a, b) => b.priority - a.priority)
    .map((f) => ({
      id: f.id,
      name: f.name,
      status: f.status,
      isSelected: !botStudioEnabled ? true : f.status === FlowStatus.Live,
      priority: f.priority,
    }));

  answerSelection.value = answers.value
    .sort((a1, a2) => a1.title.localeCompare(a2.title))
    .map((f) => ({
      id: f.id,
      name: f.title,
      status: f.status,
      isSelected: !botStudioEnabled ? true : f.status === FlowStatus.Live,
      priority: 1,
    }));
  loaded.value = true;
}

function handleBotUserUpdated(updateUser: BotUserViewModel) {
  Object.entries(updateUser.data).forEach(([key, val]) => applyBotUserUpdateToContact(key, val));
  contact.value = { ...contact.value }; // hack to make it reactive as nullable dates and dictionary entries not reactive
}

function handleContactUpdated() {
  updateBotUser('appUser.type', contact.value.type);
  updateBotUser('appUser.givenName', contact.value.givenName);
  updateBotUser('appUser.surname', contact.value.surname);
  updateBotUser('appUser.name', getName());
  updateBotUser('appUser.email', contact.value.email);
  updateBotUser('appUser.phoneNumber', contact.value.phoneNumber);
  updateBotUser('appUser.firstSeen', contact.value.firstSeenUtc);
  updateBotUser('appUser.lastSeen', contact.value.lastSeenUtc);
  updateBotUser('appUser.lastContacted', contact.value.lastContactedUtc);
  updateBotUser('appUser.lastHeardFrom', contact.value.lastHeardFromUtc);

  Object.entries(contact.value.customAttributes).forEach(([key, val]) => {
    updateBotUser(`appUser.customAttributes.${key}`, val);
  });
}

function resetContact() {
  contact.value = {
    id: '',
    type: ContactType.Lead,
    name: '',
    givenName: '',
    surname: '',
    email: '',
    phoneNumber: '',
    avatarUrl: '',
    firstSeenUtc: undefined,
    lastHeardFromUtc: undefined,
    lastSeenUtc: undefined,
    lastContactedUtc: undefined,
    customAttributes: {},
  };
  handleContactUpdated();
  resetCustomAttributes();
}

function isFlowSelectorVisible() {
  return flows.value.length > 0;
}

function isAnswerSelectorVisible() {
  return answers.value.length > 0;
}

function enableCurrentFlowOrAnswer() {
  if (props.flowToPreview) {
    const flow = flows.value.find((f) => f.id === props.flowToPreview);
    if (flow) {
      flow.status = FlowStatus.Live;
    }
  }

  if (props.answerToPreview) {
    const answer = answers.value.find((f) => f.id === props.answerToPreview);
    if (answer) {
      answer.status = FlowStatus.Live;
    }
  }
}

function resetCustomAttributes() {
  Object.entries(botUser.value.data).forEach(([key, val]) => {
    if (key.startsWith('appUser.customAttributes.')) {
      updateBotUser(key, undefined);
    }
  });
}

function getName() {
  let name = '';
  if (contact.value.givenName) {
    name = contact.value.givenName;
  }
  if (contact.value.surname) {
    name = `${name} ${contact.value.surname}`;
  }
  return name;
}

function applyBotUserUpdateToContact(name: string, value: any) {
  if (name.startsWith('appUser.customAttributes')) {
    const attributeName = name.substring(name.lastIndexOf('.') + 1);
    contact.value.customAttributes[attributeName] = value;
  } else if (name === 'appUser.name') {
    contact.value.name = value;
  } else if (name === 'appUser.givenName') {
    contact.value.givenName = value;
  } else if (name === 'appUser.surname') {
    contact.value.surname = value;
  } else if (name === 'appUser.email') {
    contact.value.email = value;
  } else if (name === 'appUser.phoneNumber') {
    contact.value.phoneNumber = value;
  } else if (name === 'appUser.lastSeen') {
    contact.value.lastSeenUtc = value;
  } else if (name === 'appUser.firstSeen') {
    contact.value.firstSeenUtc = value;
  } else if (name === 'appUser.lastContacted') {
    contact.value.lastContactedUtc = value;
  } else if (name === 'appUser.lastHeardFrom') {
    contact.value.lastHeardFromUtc = value;
  }
}

function updateBotUser(key: string, value: any) {
  botUser.value.data[key] = value;
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_modal = _resolveComponent("n-modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_n_modal, {
      class: "bot-test-tool-modal",
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (emit('cancel')))
    }, {
      title: _withCtx(() => [...(_cache[5] || (_cache[5] = [
        _createElementVNode("h2", null, "Bot Preview", -1)
      ]))]),
      body: _withCtx(() => [
        (!loaded.value)
          ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 0 }))
          : _createCommentVNode("", true),
        (loaded.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(BotPreviewMessagingClientContainer, {
                  user: botUser.value,
                  flows: selectedFlows.value,
                  answers: selectedAnswers.value,
                  currentTime: currentTime.value,
                  onUserUpdated: handleBotUserUpdated,
                  onChannelChanged: resetContact
                }, null, 8, ["user", "flows", "answers", "currentTime"])
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(TimeSelector, {
                  modelValue: currentTime.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentTime).value = $event))
                }, null, 8, ["modelValue"]),
                _createVNode(ContactDetails, {
                  modelValue: contact.value,
                  "onUpdate:modelValue": [
                    _cache[1] || (_cache[1] = ($event: any) => ((contact).value = $event)),
                    handleContactUpdated
                  ]
                }, null, 8, ["modelValue"]),
                (isFlowSelectorVisible())
                  ? (_openBlock(), _createBlock(FlowSelector, {
                      key: 0,
                      id: "flow-selector",
                      modelValue: flowSelection.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((flowSelection).value = $event)),
                      icon: "FlowIcon",
                      title: "Flows",
                      subtitle: "Choose which flows to test",
                      tooltip: "Each flow can be enabled or disabled using the toggle or reordered to alter their priority. Changes made here only affect this preview tool and will not be seen by your customers."
                    }, null, 8, ["modelValue"]))
                  : _createCommentVNode("", true),
                (isAnswerSelectorVisible())
                  ? (_openBlock(), _createBlock(FlowSelector, {
                      key: 1,
                      id: "answer-selector",
                      modelValue: answerSelection.value,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((answerSelection).value = $event)),
                      icon: "AnswerIcon",
                      title: "FAQs",
                      subtitle: "Choose which FAQs to test",
                      tooltip: "Each FAQ can be enabled or disabled using the toggle. Changes made here only affect this preview tool and will not be seen by your customers.",
                      reorderable: false
                    }, null, 8, ["modelValue"]))
                  : _createCommentVNode("", true)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})